import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { Search, Filter, Database, Link, Unlink, CheckCircle, Clock, Circle, Dumbbell, Activity, MoreVertical, Eye, Trash2, AlertTriangle, LinkIcon, Edit3, Copy, Home, RefreshCw } from 'lucide-react';
import { getExerciseAnimationUrl } from '../../utils/exerciseImages';
import FullScreenWorkoutEditor from './FullScreenWorkoutEditor';
import SaveAsFlexibleModal from './SaveAsFlexibleModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * WorkoutInstancesExplorer - Shows all workout instances (actual sessions performed by clients)
 * 
 * Columns:
 * - Client
 * - Date
 * - Status (finished, in_progress, not_started)
 * - Exercise count
 * - Volume (total sets × reps × weight)
 * - Linked booking
 * - Workout ID
 * - Actions
 * 
 * Filters:
 * - Client
 * - Date range
 * - Status
 * - Linked / Unlinked
 * 
 * Primary Actions (always visible):
 * - View Workout
 * - Edit Workout
 * 
 * Secondary Actions (inside ⋮ menu, admin only):
 * - Reassign Booking
 * - Mark Invalid
 * - Delete Workout
 */
const WorkoutInstancesExplorer = ({ user, exercises = [], templates = [], onRefresh }) => {
  const [workoutsData, setWorkoutsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    linked: '',
    client_id: '',
    date_from: '',
    date_to: ''
  });
  
  // Admin action states
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [showActionsMenu, setShowActionsMenu] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewModalWorkout, setViewModalWorkout] = useState(null); // Full workout data for modal
  const [viewModalLoading, setViewModalLoading] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [saveFlexWorkout, setSaveFlexWorkout] = useState(null);
  const [availableBookings, setAvailableBookings] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Workout editor state
  const [editingWorkout, setEditingWorkout] = useState(null);
  const [localTemplates, setLocalTemplates] = useState([]);
  
  // Check if user is admin
  const isAdmin = user?.role === 'administrator';
  
  // Ref for click-outside detection
  const dropdownRef = useRef(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowActionsMenu(null);
      }
    };
    
    if (showActionsMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showActionsMenu]);

  const fetchWorkouts = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.linked) params.append('linked', filters.linked);
      if (filters.client_id) params.append('client_id', filters.client_id);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      params.append('limit', '200');
      
      const res = await axios.get(`${API}/workouts/instances?${params.toString()}`, { headers });
      setWorkoutsData(res.data);
    } catch (error) {
      console.error('Error fetching workouts:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchWorkouts();
  }, [fetchWorkouts]);

  // Calculate estimated 1RM using Brzycki formula
  // 1RM = weight × (1 + reps / 30) simplified
  const calculateEstimated1RM = (weight, reps) => {
    if (!weight || !reps || reps <= 0) return 0;
    return weight * (1 + reps / 30);
  };

  // Get best set for an exercise using 1RM estimation
  // Returns the set with highest estimated 1RM (not just highest weight)
  const getBestSet = (exercise) => {
    const actualSets = exercise.actual_sets || [];
    if (actualSets.length === 0) return null;
    
    let bestSet = null;
    let bestScore = 0;
    
    actualSets.forEach((set, index) => {
      const weight = set.weight || 0;
      const reps = set.reps || 0;
      const score = calculateEstimated1RM(weight, reps);
      
      if (score > bestScore) {
        bestScore = score;
        bestSet = { ...set, index, estimated1RM: score };
      }
    });
    
    return bestSet;
  };


  // Admin action handlers
  const handleViewWorkout = async (workout) => {
    setSelectedWorkout(workout);
    setShowActionsMenu(null);
    setViewModalLoading(true);
    setShowViewModal(true);
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/workouts/${workout.id}`, { headers });
      setViewModalWorkout(res.data);
    } catch (error) {
      console.error('Error fetching workout details:', error);
      setViewModalWorkout(null);
    } finally {
      setViewModalLoading(false);
    }
  };

  // Determine editor mode based on workout state
  const getEditorMode = (workout) => {
    if (!workout) return 'plan';
    
    // COMPLETED: is_finished = true → Results mode (correction only)
    if (workout.is_finished || workout.status === 'finished') {
      return 'results';
    }
    
    // Check workout status for in_progress
    if (workout.status === 'in_progress') {
      return 'entry';
    }
    
    // Check booking date if available
    if (workout.booking_date) {
      const now = new Date();
      const bookingStart = new Date(`${workout.booking_date}T${workout.booking_time || '00:00'}:00`);
      
      if (now < bookingStart) {
        return 'plan'; // Future booking → Full edit mode
      } else {
        return 'entry'; // Past/current booking → Results entry mode
      }
    }
    
    // Fallback based on db_status
    if (workout.db_status === 'scheduled') {
      return 'plan';
    }
    
    return 'entry';
  };

  // Handle editing a workout - opens full screen editor
  const handleEditWorkout = async (workout) => {
    setShowActionsMenu(null);
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch full workout data and templates in parallel
      const [workoutRes, templatesRes] = await Promise.all([
        axios.get(`${API}/workouts/${workout.id}`, { headers }),
        templates.length === 0 ? axios.get(`${API}/workouts?client_id=template`, { headers }) : Promise.resolve({ data: templates })
      ]);
      
      const fullWorkout = workoutRes.data;
      
      // Cache templates locally if fetched
      if (templatesRes.data && Array.isArray(templatesRes.data)) {
        setLocalTemplates(templatesRes.data);
      }
      
      // Create a mock booking object from workout data for the editor
      const mockBooking = {
        id: workout.booking_id || null,
        client_id: workout.client_id,
        client_name: workout.client_name,
        date: workout.booking_date || workout.date,
        start_time: workout.booking_time?.split(' - ')[0] || '09:00',
        end_time: workout.booking_time?.split(' - ')[1] || '10:00',
      };
      
      const mode = getEditorMode(workout);
      setEditingWorkout({ booking: mockBooking, workout: fullWorkout, mode });
    } catch (error) {
      console.error('Error fetching workout for editing:', error);
      alert('Failed to load workout for editing');
    }
  };

  // Handle saving workout from editor - matches FullScreenWorkoutEditor callback signature
  const handleSaveWorkout = async (booking, existingWorkout, exerciseData, workoutTimes, mode) => {
    const safeInt = (val, fallback) => {
      const num = parseInt(val);
      return isNaN(num) ? fallback : num;
    };
    const safeFloat = (val, fallback) => {
      const num = parseFloat(val);
      return isNaN(num) ? fallback : num;
    };
    
    const formattedExercises = exerciseData.map(ex => ({
      exercise_id: ex.exercise_id || '',
      exercise_name: ex.exercise_name || 'Unknown Exercise',
      sets: safeInt(ex.target_sets, 4),
      target_sets: safeInt(ex.target_sets, 4),
      reps: safeInt(ex.target_reps, 12),
      target_reps: safeInt(ex.target_reps, 12),
      weight: safeFloat(ex.target_weight, 0),
      target_weight: safeFloat(ex.target_weight, 0),
      rest_time: safeInt(ex.rest_time, 120),
      notes: ex.notes || '',
      image_url: ex.image_url || '',
      animation_webp: ex.animation_webp || '',
      instructions: ex.instructions || '',
      instructions_es: ex.instructions_es || '',
      muscle_groups: ex.muscle_groups || [],
      difficulty: ex.difficulty || '',
      equipment: ex.equipment || '',
      is_superset: ex.is_superset || false,
      superset_group: ex.superset_group || 'A',
      actual_sets: ex.actual_sets || [],
      note: ex.note || ''
    }));
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Use existing workout ID (from the workout we're editing)
      const workoutId = existingWorkout?.id;
      
      if (!workoutId) {
        throw new Error('No workout ID found');
      }
      
      // Save exercises using the set-exercises endpoint
      await axios.put(`${API}/workouts/${workoutId}/set-exercises`, formattedExercises, { headers });
      
      // Save times if in live/results mode
      if (mode !== 'plan' && (workoutTimes.total_time > 0 || workoutTimes.rest_time > 0)) {
        const activeTime = workoutTimes.total_time - workoutTimes.rest_time;
        await axios.put(`${API}/workouts/${workoutId}`, {
          total_time: workoutTimes.total_time,
          rest_time: workoutTimes.rest_time,
          duration: activeTime > 0 ? activeTime : 0
        }, { headers });
      }
      
      setEditingWorkout(null);
      fetchWorkouts();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error saving workout:', error);
      throw error; // Re-throw to let FullScreenWorkoutEditor handle the error display
    }
  };

  const handleReplayWorkout = async (workout) => {
    setShowActionsMenu(null);
    if (!workout?.is_finished) {
      alert('Only finished sessions can be repeated.');
      return;
    }
    const today = new Date().toISOString().split('T')[0];
    const dateStr = window.prompt(
      `↻ Repeat Session\n\nCreates a new session with the exact per-set structure (reps, weight, rest) of the performed exercises.\n\nWhich date? (YYYY-MM-DD)`,
      today
    );
    if (!dateStr) return;
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/workouts/${workout.id}/replay`,
        { date: dateStr, booking_id: null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchWorkouts();
      alert(`✅ Repeated session created for ${dateStr}.`);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not create the repeated session';
      alert(detail);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveAsTemplate = async (workout) => {
    setShowActionsMenu(null);
    const defaultName = workout.template_name || `Template ${workout.date || ''}`.trim();
    const name = window.prompt(
      `📋 Save as Template\n\nSaves this workout's exercises as a reusable template (available in Templates).\n\nTemplate name:`,
      defaultName
    );
    if (name === null) return;
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/workouts/${workout.id}/save-as-template`,
        { name: name.trim() || defaultName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (onRefresh) onRefresh();
      alert(`✅ Template "${res.data?.name}" created with ${res.data?.exercise_count} exercises.`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not save as template');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveAsFlexible = (workout) => {
    setShowActionsMenu(null);
    setSaveFlexWorkout(workout);
  };

  const handleReassignBooking = async (workout) => {
    // Status guard: block in_progress
    if (workout.status === 'in_progress') {
      alert('Cannot reassign a workout that is currently in progress. Finish or cancel the session first.');
      setShowActionsMenu(null);
      return;
    }
    
    setSelectedWorkout(workout);
    setShowActionsMenu(null);
    
    // Fetch available bookings
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(
        `${API}/workouts/admin/available-bookings?client_id=${workout.client_id}`,
        { headers }
      );
      setAvailableBookings(res.data.bookings || []);
      setShowReassignModal(true);
    } catch (error) {
      console.error('Error fetching available bookings:', error);
      alert('Failed to load available bookings');
    }
  };

  const confirmReassign = async (bookingId) => {
    if (!selectedWorkout) return;
    
    // Completed workout: require explicit confirmation
    const isCompleted = selectedWorkout.status === 'finished' || selectedWorkout.is_finished;
    let forceFlag = false;
    if (isCompleted && bookingId) {
      const confirmed = window.confirm(
        'This workout is already completed. Moving it will change its linked booking date.\n\nAll exercises, sets, and data will be preserved.\n\nContinue?'
      );
      if (!confirmed) return;
      forceFlag = true;
    }
    
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const url = `${API}/workouts/admin/${selectedWorkout.id}/reassign-booking?booking_id=${bookingId || ''}${forceFlag ? '&force=true' : ''}`;
      const res = await axios.put(url, {}, { headers });
      
      setShowReassignModal(false);
      setSelectedWorkout(null);
      fetchWorkouts();
      
      // Show success with preserved exercise count
      const data = res.data;
      if (data.exercises_preserved) {
        alert(`Workout moved successfully (${data.exercises_preserved} exercises preserved)`);
      }
    } catch (error) {
      console.error('Error reassigning booking:', error);
      const detail = error.response?.data?.detail || 'Failed to reassign booking';
      alert(detail);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteWorkout = (workout) => {
    setSelectedWorkout(workout);
    setShowDeleteConfirm(true);
    setShowActionsMenu(null);
  };

  const confirmDelete = async () => {
    if (!selectedWorkout) return;
    
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.delete(`${API}/workouts/admin/${selectedWorkout.id}`, { headers });
      
      setShowDeleteConfirm(false);
      setSelectedWorkout(null);
      fetchWorkouts();
    } catch (error) {
      console.error('Error deleting workout:', error);
      alert('Failed to delete workout');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkInvalid = async (workout, validForMetrics) => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.put(
        `${API}/workouts/admin/${workout.id}/mark-invalid?valid_for_metrics=${validForMetrics}`,
        {},
        { headers }
      );
      
      setShowActionsMenu(null);
      fetchWorkouts();
    } catch (error) {
      console.error('Error marking workout:', error);
      alert('Failed to update workout');
    } finally {
      setActionLoading(false);
    }
  };

  // Calculate volume for a workout (sum of sets × reps × weight)
  const calculateVolume = (workout) => {
    if (!workout.exercises) return 0;
    let totalVolume = 0;
    workout.exercises.forEach(ex => {
      (ex.actual_sets || []).forEach(set => {
        const weight = set.weight || 0;
        const reps = set.reps || 0;
        totalVolume += weight * reps;
      });
    });
    return totalVolume;
  };

  // Summary stats
  const stats = useMemo(() => {
    if (!workoutsData?.workouts) return null;
    const workouts = workoutsData.workouts;
    return {
      total: workouts.length,
      finished: workouts.filter(w => w.status === 'finished').length,
      in_progress: workouts.filter(w => w.status === 'in_progress').length,
      not_started: workouts.filter(w => w.status === 'not_started').length,
      linked: workouts.filter(w => w.has_booking).length,
      unlinked: workouts.filter(w => !w.has_booking).length
    };
  }, [workoutsData]);

  const clearFilters = () => {
    setFilters({
      status: '',
      linked: '',
      client_id: '',
      date_from: '',
      date_to: ''
    });
  };

  const hasActiveFilters = filters.status || filters.linked || filters.client_id || filters.date_from || filters.date_to;

  return (
    <div className="space-y-6" data-testid="workout-instances-explorer">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Dumbbell className="w-6 h-6 text-blue-400" />
            Workout Instances
          </h2>
          <p className="text-blue-400 text-sm mt-1">
            All workout sessions performed by clients
          </p>
        </div>
        <button
          onClick={fetchWorkouts}
          className="btn-secondary flex items-center gap-2"
          disabled={loading}
        >
          <Activity className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Summary Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div className="glass-card p-4 text-center">
            <div className="text-blue-300 text-xs mb-1">Total</div>
            <div className="text-2xl font-bold text-white">{stats.total}</div>
          </div>
          <div className="glass-card p-4 text-center bg-green-500/10">
            <div className="text-green-300 text-xs mb-1 flex items-center justify-center gap-1">
              <CheckCircle className="w-3 h-3" /> Finished
            </div>
            <div className="text-2xl font-bold text-green-400">{stats.finished}</div>
          </div>
          <div className="glass-card p-4 text-center bg-yellow-500/10">
            <div className="text-yellow-300 text-xs mb-1 flex items-center justify-center gap-1">
              <Clock className="w-3 h-3" /> In Progress
            </div>
            <div className="text-2xl font-bold text-yellow-400">{stats.in_progress}</div>
          </div>
          <div className="glass-card p-4 text-center bg-gray-500/10">
            <div className="text-gray-300 text-xs mb-1 flex items-center justify-center gap-1">
              <Circle className="w-3 h-3" /> Not Started
            </div>
            <div className="text-2xl font-bold text-gray-400">{stats.not_started}</div>
          </div>
          <div className="glass-card p-4 text-center bg-purple-500/10">
            <div className="text-purple-300 text-xs mb-1 flex items-center justify-center gap-1">
              <Link className="w-3 h-3" /> Linked
            </div>
            <div className="text-2xl font-bold text-purple-400">{stats.linked}</div>
          </div>
          <div className="glass-card p-4 text-center bg-red-500/10">
            <div className="text-red-300 text-xs mb-1 flex items-center justify-center gap-1">
              <Unlink className="w-3 h-3" /> Unlinked
            </div>
            <div className="text-2xl font-bold text-red-400">{stats.unlinked}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="glass-card p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-blue-400" />
          <span className="text-white font-medium">Filters</span>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-blue-400 hover:text-blue-300 text-xs ml-auto"
            >
              Clear All
            </button>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          {/* Client Filter */}
          <div>
            <label className="block text-xs text-gray-400 mb-1">Client</label>
            <select
              value={filters.client_id}
              onChange={(e) => setFilters(f => ({ ...f, client_id: e.target.value }))}
              className="w-full px-3 py-2 bg-blue-900/40 border border-blue-500/30 rounded-lg text-white text-sm"
              data-testid="workout-filter-client"
            >
              <option value="">All Clients</option>
              {workoutsData?.available_clients?.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-xs text-gray-400 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
              className="w-full px-3 py-2 bg-blue-900/40 border border-blue-500/30 rounded-lg text-white text-sm"
              data-testid="workout-filter-status"
            >
              <option value="">All Status</option>
              <option value="finished">Finished</option>
              <option value="in_progress">In Progress</option>
              <option value="not_started">Not Started</option>
            </select>
          </div>

          {/* Linked Filter */}
          <div>
            <label className="block text-xs text-gray-400 mb-1">Booking Link</label>
            <select
              value={filters.linked}
              onChange={(e) => setFilters(f => ({ ...f, linked: e.target.value }))}
              className="w-full px-3 py-2 bg-blue-900/40 border border-blue-500/30 rounded-lg text-white text-sm"
              data-testid="workout-filter-linked"
            >
              <option value="">All</option>
              <option value="linked">Linked (has booking)</option>
              <option value="unlinked">Unlinked (orphaned)</option>
            </select>
          </div>

          {/* Date From */}
          <div>
            <label className="block text-xs text-gray-400 mb-1">Date From</label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))}
              className="w-full px-3 py-2 bg-blue-900/40 border border-blue-500/30 rounded-lg text-white text-sm"
            />
          </div>

          {/* Date To */}
          <div>
            <label className="block text-xs text-gray-400 mb-1">Date To</label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value }))}
              className="w-full px-3 py-2 bg-blue-900/40 border border-blue-500/30 rounded-lg text-white text-sm"
            />
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="glass-card overflow-hidden">
        {loading ? (
          <div className="text-center py-12 text-blue-300">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
            Loading workouts...
          </div>
        ) : (
          <>
            <div className="p-4 border-b border-blue-500/20">
              <span className="text-blue-300 text-sm">
                Showing {workoutsData?.total || 0} workout instances
              </span>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-blue-900/30">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-blue-300 uppercase">Client</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-blue-300 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-blue-300 uppercase">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-blue-300 uppercase">Exercises</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-blue-300 uppercase">Volume</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-blue-300 uppercase">Booking</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-blue-300 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-blue-500/10">
                  {workoutsData?.workouts?.map((workout) => (
                    <tr 
                      key={workout.id}
                      className={`hover:bg-blue-900/20 transition ${workout.valid_for_metrics === false ? 'opacity-50' : ''}`}
                    >
                      <td className="px-4 py-3 text-white font-medium">
                        {workout.client_name || 'Unknown'}
                        {workout.valid_for_metrics === false && (
                          <span className="ml-2 text-xs text-orange-400">(Invalid)</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-blue-200">
                        {workout.date || '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                          workout.status === 'finished' ? 'bg-green-500/20 text-green-300' :
                          workout.status === 'in_progress' ? 'bg-yellow-500/20 text-yellow-300' :
                          'bg-gray-500/20 text-gray-300'
                        }`}>
                          {workout.status === 'finished' && <CheckCircle className="w-3 h-3" />}
                          {workout.status === 'in_progress' && <Clock className="w-3 h-3" />}
                          {workout.status === 'not_started' && <Circle className="w-3 h-3" />}
                          {workout.status === 'finished' ? 'Finished' :
                           workout.status === 'in_progress' ? 'In Progress' : 'Not Started'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-blue-200">
                        {workout.exercise_count || 0}
                      </td>
                      <td className="px-4 py-3 text-right text-blue-200 font-mono text-sm">
                        {workout.total_volume ? `${(workout.total_volume / 1000).toFixed(1)}k kg` : '-'}
                      </td>
                      <td className="px-4 py-3">
                        {workout.has_booking ? (
                          <span className="inline-flex items-center gap-1 text-purple-300 text-sm">
                            <Link className="w-3 h-3" />
                            {workout.booking_date} {workout.booking_time}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-400 text-sm">
                            <Unlink className="w-3 h-3" />
                            Unlinked
                          </span>
                        )}
                      </td>
                      {/* Primary Actions: View and Edit - Available to all users */}
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          {/* View Button */}
                          <button
                            onClick={() => handleViewWorkout(workout)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-500/20 text-blue-300 rounded-lg hover:bg-blue-500/30 transition text-sm"
                            data-testid={`view-workout-${workout.id}`}
                            title="View Workout"
                          >
                            <Eye className="w-4 h-4" />
                            <span className="hidden sm:inline">View</span>
                          </button>
                          
                          {/* Edit Button - Dumbbell icon to match Bookings tab */}
                          <button
                            onClick={() => handleEditWorkout(workout)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-yellow-500/20 text-yellow-300 rounded-lg hover:bg-yellow-500/30 transition text-sm"
                            data-testid={`edit-workout-${workout.id}`}
                            title={workout.is_finished || workout.status === 'finished' ? 'Edit Results' : 'Edit Workout'}
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M20.57 14.86L22 13.43 20.57 12 17 15.57 8.43 7 12 3.43 10.57 2 9.14 3.43 7.71 2 5.57 4.14 4.14 2.71 2.71 4.14l1.43 1.43L2 7.71l1.43 1.43L2 10.57 3.43 12 7 8.43 15.57 17 12 20.57 13.43 22l1.43-1.43L16.29 22l2.14-2.14 1.43 1.43 1.43-1.43-1.43-1.43L22 16.29z"/>
                            </svg>
                            <span className="hidden sm:inline">Edit</span>
                          </button>
                          
                          {/* Admin Dropdown Menu - Secondary actions only */}
                          {isAdmin && (
                            <div className="relative" ref={showActionsMenu === workout.id ? dropdownRef : null}>
                              <button
                                onClick={() => setShowActionsMenu(showActionsMenu === workout.id ? null : workout.id)}
                                className="p-1.5 hover:bg-slate-500/20 rounded transition"
                                data-testid={`workout-actions-${workout.id}`}
                                title="More actions"
                              >
                                <MoreVertical className="w-4 h-4 text-slate-400" />
                              </button>
                              
                              {/* Actions Dropdown - Secondary/Admin actions only */}
                              {showActionsMenu === workout.id && (
                                <div className="absolute right-0 mt-1 w-48 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-20">
                                  {workout.is_finished && (
                                    <button
                                      onClick={() => handleReplayWorkout(workout)}
                                      className="w-full px-4 py-2 text-left text-sm text-purple-200 hover:bg-purple-500/20 flex items-center gap-2"
                                      data-testid={`replay-action-${workout.id}`}
                                    >
                                      <RefreshCw className="w-4 h-4" />
                                      Repeat Session
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleSaveAsTemplate(workout)}
                                    className="w-full px-4 py-2 text-left text-sm text-emerald-200 hover:bg-emerald-500/20 flex items-center gap-2"
                                    data-testid={`save-as-template-${workout.id}`}
                                  >
                                    <Copy className="w-4 h-4" /> Save as Template
                                  </button>
                                  <button
                                    onClick={() => handleSaveAsFlexible(workout)}
                                    className="w-full px-4 py-2 text-left text-sm text-amber-200 hover:bg-amber-500/20 flex items-center gap-2"
                                    data-testid={`save-as-flexible-${workout.id}`}
                                  >
                                    <Home className="w-4 h-4" /> Save as Free Routine
                                  </button>
                                  <button
                                    onClick={() => handleReassignBooking(workout)}
                                    className="w-full px-4 py-2 text-left text-sm text-white hover:bg-blue-500/20 flex items-center gap-2"
                                  >
                                    <LinkIcon className="w-4 h-4" /> Move to Booking
                                  </button>
                                  {workout.valid_for_metrics !== false ? (
                                    <button
                                      onClick={() => handleMarkInvalid(workout, false)}
                                      className="w-full px-4 py-2 text-left text-sm text-orange-300 hover:bg-orange-500/20 flex items-center gap-2"
                                    >
                                      <AlertTriangle className="w-4 h-4" /> Mark Invalid
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => handleMarkInvalid(workout, true)}
                                      className="w-full px-4 py-2 text-left text-sm text-green-300 hover:bg-green-500/20 flex items-center gap-2"
                                    >
                                      <CheckCircle className="w-4 h-4" /> Mark Valid
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleDeleteWorkout(workout)}
                                    className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-red-500/20 flex items-center gap-2"
                                  >
                                    <Trash2 className="w-4 h-4" /> Delete Workout
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {(!workoutsData?.workouts || workoutsData.workouts.length === 0) && (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                        <Dumbbell className="w-12 h-12 mx-auto mb-2 opacity-30" />
                        No workout instances found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* View Workout Modal - Full Details with Exercises, GIFs, and Best Sets */}
      {showViewModal && selectedWorkout && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999] p-4" onClick={() => setShowViewModal(false)}>
          <div 
            className="bg-gray-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-4 border-b border-gray-700 flex justify-between items-center flex-shrink-0 bg-gradient-to-r from-blue-900/50 to-purple-900/50">
              <div>
                <h3 className="text-lg font-bold text-white">📊 Workout Details</h3>
                <p className="text-sm text-gray-400">{selectedWorkout.client_name} • {selectedWorkout.date}</p>
              </div>
              <button 
                onClick={() => {
                  setShowViewModal(false);
                  setViewModalWorkout(null);
                }} 
                className="text-gray-400 hover:text-white p-2 text-xl font-bold"
              >
                ✕
              </button>
            </div>
            
            {/* Scrollable Content */}
            <div className="p-4 overflow-y-auto flex-1">
              {viewModalLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mr-3"></div>
                  <span className="text-blue-300">Loading workout details...</span>
                </div>
              ) : viewModalWorkout ? (
                <>
                  {/* Summary Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                    <div className="bg-gray-700/50 p-3 rounded-lg text-center">
                      <div className="text-gray-400 text-xs">Status</div>
                      <div className={`font-medium ${
                        selectedWorkout.status === 'finished' ? 'text-green-400' :
                        selectedWorkout.status === 'in_progress' ? 'text-yellow-400' : 'text-gray-400'
                      }`}>
                        {selectedWorkout.status === 'finished' ? '✅ Finished' :
                         selectedWorkout.status === 'in_progress' ? '🔄 In Progress' : 'Not Started'}
                      </div>
                    </div>
                    <div className="bg-gray-700/50 p-3 rounded-lg text-center">
                      <div className="text-gray-400 text-xs">Exercises</div>
                      <div className="text-white font-bold text-xl">{viewModalWorkout.exercises?.length || 0}</div>
                    </div>
                    <div className="bg-purple-900/30 p-3 rounded-lg text-center">
                      <div className="text-purple-300 text-xs">Total Volume</div>
                      <div className="text-purple-200 font-bold text-xl">
                        {(() => {
                          const vol = (viewModalWorkout.exercises || []).reduce((sum, ex) => {
                            return sum + (ex.actual_sets || []).reduce((s, set) => 
                              s + ((set.weight || 0) * (set.reps || 0)), 0);
                          }, 0);
                          return vol > 1000 ? `${(vol / 1000).toFixed(1)}k kg` : `${vol} kg`;
                        })()}
                      </div>
                    </div>
                    <div className="bg-gray-700/50 p-3 rounded-lg text-center">
                      <div className="text-gray-400 text-xs">Duration</div>
                      <div className="text-white font-bold text-xl">
                        {viewModalWorkout.total_time ? `${Math.round(viewModalWorkout.total_time)} min` : '-'}
                      </div>
                    </div>
                    <div className="bg-emerald-900/30 p-3 rounded-lg text-center">
                      <div className="text-emerald-300 text-xs">Effective</div>
                      <div className="text-emerald-200 font-bold text-xl">
                        {(() => {
                          // Effective = total − rest. Prefer stored effective_time,
                          // else derive from stored rest_time, else sum per-set rest.
                          if (viewModalWorkout.effective_time != null) {
                            return `${Math.round(viewModalWorkout.effective_time)} min`;
                          }
                          const total = viewModalWorkout.total_time;
                          if (!total) return '-';
                          let restMin = viewModalWorkout.rest_time;
                          if (restMin == null) {
                            let restSec = 0;
                            (viewModalWorkout.exercises || []).forEach((ex) =>
                              (ex.actual_sets || []).forEach((s) => { restSec += (s.rest_time || 0); })
                            );
                            restMin = restSec / 60;
                          }
                          return `${Math.max(0, Math.round(total - restMin))} min`;
                        })()}
                      </div>
                    </div>
                  </div>
                  
                  {/* Session Rating */}
                  {viewModalWorkout.session_rating && (
                    <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-3 mb-4 text-center">
                      <span className="text-yellow-300 text-sm">Session Rating: </span>
                      <span className="text-yellow-200 font-bold text-lg">{viewModalWorkout.session_rating}/10 ⭐</span>
                    </div>
                  )}

                  {/* Client's written feedback */}
                  {viewModalWorkout.session_notes && viewModalWorkout.session_notes.trim() && (
                    <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3 mb-4" data-testid="instance-session-notes">
                      <p className="text-blue-300 text-sm mb-1">📝 Client feedback</p>
                      <p className="text-white text-sm whitespace-pre-wrap break-words">{viewModalWorkout.session_notes}</p>
                    </div>
                  )}
                  
                  {/* Invalid Workout Warning */}
                  {selectedWorkout.valid_for_metrics === false && (
                    <div className="bg-orange-500/20 border border-orange-500/50 p-3 rounded-lg text-orange-300 text-sm mb-4">
                      ⚠️ This workout is marked as invalid and excluded from PR calculations.
                    </div>
                  )}
                  
                  {/* Exercise List */}
                  <div className="mb-4">
                    <h4 className="text-blue-200 font-medium mb-3 text-sm flex items-center gap-2">
                      🏋️ Exercises ({viewModalWorkout.exercises?.length || 0})
                    </h4>
                    <div className="space-y-3">
                      {(viewModalWorkout.exercises || []).map((exercise, exIdx) => {
                        const bestSet = getBestSet(exercise);
                        const actualSets = exercise.actual_sets || [];
                        const exerciseVolume = actualSets.reduce((sum, set) => 
                          sum + ((set.weight || 0) * (set.reps || 0)), 0);
                        
                        return (
                          <div 
                            key={exIdx} 
                            className="bg-gray-700/30 rounded-lg p-3 border border-gray-600/30"
                          >
                            <div className="flex gap-3">
                              {/* Exercise GIF/Image - Uses animation_webp with fallback */}
                              {(() => {
                                const animUrl = getExerciseAnimationUrl(exercise);
                                return (
                                  <div className="flex-shrink-0 w-20 h-20 bg-black/30 rounded-lg overflow-hidden">
                                    {animUrl ? (
                                      <img
                                        src={animUrl}
                                        alt={exercise.exercise_name || exercise.exercise_name_es}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                                        }}
                                      />
                                    ) : null}
                                    <div 
                                      className={`w-full h-full items-center justify-center text-3xl ${animUrl ? 'hidden' : 'flex'}`}
                                      style={{ display: animUrl ? 'none' : 'flex' }}
                                    >
                                      🏋️
                                    </div>
                                  </div>
                                );
                              })()}
                              
                              {/* Exercise Details */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <div>
                                    <span className="bg-blue-600 text-white text-xs font-bold px-2 py-0.5 rounded mr-2">
                                      {exIdx + 1}
                                    </span>
                                    <span className="text-white font-semibold">
                                      {exercise.exercise_name_es || exercise.exercise_name || 'Unknown Exercise'}
                                    </span>
                                    {exercise.is_superset && (
                                      <span className="ml-2 bg-orange-500/30 text-orange-300 text-xs px-2 py-0.5 rounded">
                                        🔗 Superset {exercise.superset_group || 'A'}
                                      </span>
                                    )}
                                  </div>
                                  {exerciseVolume > 0 && (
                                    <span className="text-blue-300 text-xs whitespace-nowrap">
                                      {exerciseVolume.toLocaleString()} kg
                                    </span>
                                  )}
                                </div>
                                
                                {/* Sets Display */}
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                  {actualSets.length > 0 ? actualSets.map((set, setIdx) => {
                                    const isBestSet = bestSet && setIdx === bestSet.index;
                                    
                                    return (
                                      <div 
                                        key={setIdx}
                                        className={`px-2 py-1 rounded text-sm font-mono ${
                                          isBestSet 
                                            ? 'bg-yellow-500/30 border border-yellow-500 text-yellow-200' 
                                            : 'bg-gray-600/50 text-gray-200'
                                        }`}
                                      >
                                        {set.reps || 0}×{set.weight || 0}kg
                                        {isBestSet && <span className="ml-1">⭐</span>}
                                      </div>
                                    );
                                  }) : (
                                    <span className="text-gray-500 text-sm italic">No sets recorded</span>
                                  )}
                                </div>
                                
                                {/* Best Set Info */}
                                {bestSet && (
                                  <div className="mt-2 text-yellow-400 text-xs">
                                    ⭐ Best Set: {bestSet.reps}×{bestSet.weight}kg 
                                    <span className="text-yellow-500/70 ml-1">
                                      (Est. 1RM: {bestSet.estimated1RM.toFixed(1)}kg)
                                    </span>
                                  </div>
                                )}
                                
                                {/* Exercise Note */}
                                {exercise.note && (
                                  <p className="mt-2 text-yellow-300/80 text-xs italic">📝 {exercise.note}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      
                      {(!viewModalWorkout.exercises || viewModalWorkout.exercises.length === 0) && (
                        <div className="text-center text-gray-500 py-8">
                          No exercises recorded in this workout
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Session Notes */}
                  {viewModalWorkout.notes && (
                    <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3 mb-3">
                      <p className="text-blue-300 text-xs font-medium mb-1">Session Notes</p>
                      <p className="text-white text-sm">{viewModalWorkout.notes}</p>
                    </div>
                  )}
                  
                  {/* Booking Info */}
                  <div className="bg-gray-700/30 p-3 rounded-lg">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-400">Booking:</span>
                      <span className={selectedWorkout.has_booking ? 'text-purple-300' : 'text-red-400'}>
                        {selectedWorkout.has_booking 
                          ? `${selectedWorkout.booking_date} ${selectedWorkout.booking_time}` 
                          : 'Unlinked (orphaned)'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm mt-1">
                      <span className="text-gray-400">Workout ID:</span>
                      <span className="text-gray-500 font-mono text-xs">{selectedWorkout.id}</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center text-red-400 py-8">
                  Failed to load workout details
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t border-gray-700 flex-shrink-0 bg-gray-800/50 text-center">
              <button 
                onClick={() => {
                  setShowViewModal(false);
                  setViewModalWorkout(null);
                }} 
                className="px-6 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Save as Free Routine Modal */}
      {saveFlexWorkout && (
        <SaveAsFlexibleModal
          workout={saveFlexWorkout}
          onClose={() => setSaveFlexWorkout(null)}
          onSaved={(wid, count) => {
            fetchWorkouts();
            if (onRefresh) onRefresh();
            alert(`✅ Free routine created${count ? ` with ${count} exercises` : ''}.`);
          }}
        />
      )}

      {/* Reassign Booking Modal - Using Portal for proper centering */}
      {showReassignModal && selectedWorkout && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999] p-4">
          <div className="bg-gray-800 rounded-xl max-w-lg w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-gray-700 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-bold text-white">Move Workout to Booking</h3>
                <p className="text-sm text-gray-400">{selectedWorkout.client_name} • {selectedWorkout.date}</p>
                <p className="text-xs text-blue-400 mt-1">
                  {selectedWorkout.exercise_count} exercises will be preserved
                </p>
              </div>
              <button onClick={() => setShowReassignModal(false)} className="text-gray-400 hover:text-white p-2" data-testid="close-reassign-modal">✕</button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[50vh]">
              {(selectedWorkout.status === 'finished' || selectedWorkout.is_finished) && (
                <div className="mb-3 px-3 py-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-yellow-300 text-sm">
                  This workout is completed. Moving it will require confirmation.
                </div>
              )}
              {availableBookings.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-gray-400 text-sm mb-3">Select a booking to move this workout to:</p>
                  {availableBookings.map(booking => (
                    <button
                      key={booking.id}
                      onClick={() => confirmReassign(booking.id)}
                      disabled={actionLoading}
                      className="w-full p-3 bg-gray-700/50 hover:bg-blue-500/20 rounded-lg text-left transition disabled:opacity-50"
                      data-testid={`reassign-booking-${booking.id}`}
                    >
                      <div className="text-white font-medium">{booking.client_name}</div>
                      <div className="text-gray-400 text-sm">{booking.date} at {booking.start_time}</div>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-center py-8">No available bookings found for this client</p>
              )}
              
              {selectedWorkout.has_booking && (
                <button
                  onClick={() => confirmReassign(null)}
                  disabled={actionLoading}
                  className="w-full mt-4 p-3 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-300 transition disabled:opacity-50"
                  data-testid="unlink-booking-btn"
                >
                  <Unlink className="w-4 h-4 inline mr-2" />
                  Unlink from current booking
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Modal - Using Portal for proper centering */}
      {showDeleteConfirm && selectedWorkout && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999] p-4">
          <div className="bg-gray-800 rounded-xl max-w-md w-full p-6 max-h-[90dvh] overflow-y-auto">
            <div className="text-center">
              <Trash2 className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">Delete Workout?</h3>
              <p className="text-gray-400 text-sm mb-4">
                This will permanently delete the workout for <strong className="text-white">{selectedWorkout.client_name}</strong> on {selectedWorkout.date}.
                <br />PRs will be recalculated without this workout.
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-500 transition disabled:opacity-50"
                >
                  {actionLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* Full-Screen Workout Editor */}
      {editingWorkout && (
        <FullScreenWorkoutEditor
          booking={editingWorkout.booking}
          workout={editingWorkout.workout}
          mode={editingWorkout.mode}
          exercises={exercises}
          templates={templates.length > 0 ? templates : localTemplates}
          onSave={handleSaveWorkout}
          onClose={() => setEditingWorkout(null)}
          onFinishWorkout={() => {
            setEditingWorkout(null);
            fetchWorkouts();
            if (onRefresh) onRefresh();
          }}
          clientId={editingWorkout.booking?.client_id}
        />
      )}
    </div>
  );
};

export default WorkoutInstancesExplorer;
